import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Ellipsis, Pause, Pencil, Play } from 'lucide-react';
import { featureBadgesForCompany, statusTone } from '@/entities/company';
import { Button, ContextMenu, Table, Tooltip, type MenuItem, type TableColumn } from '@/shared/ui';
import type { AdminCompany } from '../../types';
import { formatAdminCompanyDate } from '../../model/companyFilters';
import styles from './AdminCompaniesTable.module.scss';

interface AdminCompaniesTableProps {
  companies: AdminCompany[];
  isLoading?: boolean;
  onOpen?: (company: AdminCompany) => void;
  onEdit: (company: AdminCompany) => void;
  onSuspendToggle?: (company: AdminCompany) => void;
  actionsDisabled?: boolean;
}

export const AdminCompaniesTable = ({
  companies,
  isLoading,
  onEdit,
  onOpen = onEdit,
  onSuspendToggle,
  actionsDisabled,
}: AdminCompaniesTableProps) => {
  const { t, i18n } = useTranslation(['common', 'admin']);

  const columns = useMemo<TableColumn<AdminCompany>[]>(
    () => [
      {
        key: 'name',
        header: t('companies.columns.name', 'Название'),
        minWidth: 240,
        hideable: false,
        render: (company) => (
          <div className={styles['name-cell']}>
            <button type="button" className={styles['name-main']} onClick={() => onOpen(company)}>
              {company.name || t('admin.companies.unnamed', 'Без имени')}
            </button>
            {company.full_name && company.full_name !== company.name && (
              <span className={styles['name-sub']}>{company.full_name}</span>
            )}
          </div>
        ),
      },
      {
        key: 'plan',
        header: t('companies.columns.licensePlan', 'Тариф'),
        width: 110,
        render: (company) => (
          <span className={styles['chip']}>
            {t(`license.plan.${company.license_plan || 'base'}`, company.license_plan || '-')}
          </span>
        ),
      },
      {
        key: 'features',
        header: t('companies.columns.features', 'Возможности'),
        width: 220,
        render: (company) => {
          const badges = featureBadgesForCompany(company);
          if (badges.length === 0) return <span className={styles['muted']}>—</span>;

          return (
            <div className={styles['chips']}>
              {badges.map((feature) => (
                <span key={feature.key} className={styles['chip-feature']}>
                  {t(feature.i18nKey)}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        key: 'validUntil',
        header: t('companies.columns.validUntil', 'Действует до'),
        width: 140,
        render: (company) => (
          <span className={styles['muted']}>
            {company.license_valid_until
              ? formatAdminCompanyDate(company.license_valid_until, i18n.language)
              : '—'}
          </span>
        ),
      },
      {
        key: 'status',
        header: t('companies.columns.licenseStatus', 'Статус'),
        width: 180,
        render: (company) => {
          const status = company.work_suspended ? 'suspended' : company.license_status;
          const tone = statusTone(status);
          return (
            <span className={`${styles['status']} ${styles[`status--${tone}`]}`}>
              <span className={styles['status-dot']} aria-hidden="true" />
              {t(`license.status.${status}`, status)}
            </span>
          );
        },
      },
      {
        key: 'actions',
        header: '',
        visibilityLabel: t('admin.companies.actions', 'Действия'),
        hideable: false,
        isActionColumn: true,
        width: 64,
        alignContent: 'right',
        alignHeader: 'right',
        render: (company) => (
          <ContextMenu
            variant="compact"
            menuWidth={240}
            trigger={
              <Tooltip content={t('admin.companies.actions')}>
                <Button
                  size="small"
                  variant="ghost"
                  className={styles['action-button']}
                  disabled={actionsDisabled}
                  aria-label={`${t('admin.companies.actions')}: ${company.name || t('admin.companies.unnamed')}`}
                >
                  <Ellipsis size={18} aria-hidden="true" />
                </Button>
              </Tooltip>
            }
            items={[
              {
                type: 'text',
                label: t('admin.companies.edit'),
                icon: <Pencil size={16} />,
                onClick: () => onEdit(company),
              },
              ...(onSuspendToggle
                ? ([
                    { type: 'separator' },
                    {
                      type: 'text',
                      label: company.work_suspended
                        ? t('admin.companies.resume')
                        : t('admin.companies.suspend'),
                      icon: company.work_suspended ? <Play size={16} /> : <Pause size={16} />,
                      onClick: () => onSuspendToggle(company),
                    },
                  ] satisfies MenuItem[])
                : []),
            ]}
          />
        ),
      },
      {
        key: 'id',
        header: t('tableFields.id'),
        hidden: true,
        render: (company) => company.id,
      },
      {
        key: 'license_type',
        header: t('companies.columns.licenseType'),
        hidden: true,
        render: (company) => company.license_type,
      },
      {
        key: 'license_valid_from',
        header: t('admin.companies.fields.validFrom'),
        hidden: true,
        render: (company) => formatAdminCompanyDate(company.license_valid_from, i18n.language),
      },
      {
        key: 'license_seats',
        header: t('admin.companies.fields.seats'),
        hidden: true,
        render: (company) => company.license_seats,
      },
      {
        key: 'has_brand_protection',
        header: t('license.feature.brand_protection'),
        hidden: true,
        render: (company) => (company.has_brand_protection ? t('boolean.yes') : t('boolean.no')),
      },
      {
        key: 'has_continuous_red_team',
        header: t('license.feature.continuous_red_team'),
        hidden: true,
        render: (company) => (company.has_continuous_red_team ? t('boolean.yes') : t('boolean.no')),
      },
      {
        key: 'work_suspended',
        header: t('admin.companies.fields.workSuspended'),
        hidden: true,
        render: (company) => (company.work_suspended ? t('boolean.yes') : t('boolean.no')),
      },
    ],
    [actionsDisabled, i18n.language, onEdit, onOpen, onSuspendToggle, t]
  );

  return (
    <Table
      density="compact"
      columns={columns}
      data={companies}
      isLoading={isLoading}
      onRowClick={onOpen}
      className={styles['table']}
      emptyMessage={t('companies.emptyList', 'Нет доступных компаний')}
      columnVisibilityStorageKey="admin-companies"
    />
  );
};
